import * as React from 'react';
import { useAuth } from './AuthContext';
import {
  SubscriptionData,
  subscribeToSubscriptionUpdates,
  hasFeatureAccess,
  checkAsinLimit,
  PRICING_CONFIG,
  PricingTier,
} from '@/services/stripeService';

// FREE MODE: Set to true to make the app completely free (no subscription required)
// Toggle this to false when ready to enable paid subscriptions
const FREE_MODE = true;

interface SubscriptionContextValue {
  subscription: SubscriptionData | null;
  isLoading: boolean;
  isSubscribed: boolean;
  subscriptionTier: PricingTier | null;
  canAccessFeature: (feature: 'verification' | 'portfolio-health' | 'advanced-features') => boolean;
  checkAsinLimit: (asinCount: number) => { allowed: boolean; limit: number; tier: string };
  getTierConfig: (tier: PricingTier) => typeof PRICING_CONFIG[PricingTier];
}

const SubscriptionContext = React.createContext<SubscriptionContextValue | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = React.useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (!user) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    
    // Subscribe to real-time subscription updates
    const unsubscribe = subscribeToSubscriptionUpdates(user.uid, (subscriptionData) => {
      setSubscription(subscriptionData);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const isSubscribed = React.useMemo(() => {
    // In FREE_MODE, everyone is treated as subscribed
    if (FREE_MODE) return true;
    return subscription !== null && ['active', 'trialing'].includes(subscription.status);
  }, [subscription]);

  const subscriptionTier = React.useMemo(() => {
    return subscription?.tier || null;
  }, [subscription]);

  const canAccessFeature = React.useCallback(
    (feature: 'verification' | 'portfolio-health' | 'advanced-features') => {
      // In FREE_MODE, all features are accessible
      if (FREE_MODE) return true;
      return hasFeatureAccess(subscription, feature);
    },
    [subscription]
  );

  const checkAsinLimitCallback = React.useCallback(
    (asinCount: number) => {
      // In FREE_MODE, no ASIN limits
      if (FREE_MODE) return { allowed: true, limit: Infinity, tier: 'free' };
      return checkAsinLimit(subscription, asinCount);
    },
    [subscription]
  );

  const getTierConfig = React.useCallback((tier: PricingTier) => {
    return PRICING_CONFIG[tier];
  }, []);

  const value = React.useMemo<SubscriptionContextValue>(
    () => ({
      subscription,
      isLoading,
      isSubscribed,
      subscriptionTier,
      canAccessFeature,
      checkAsinLimit: checkAsinLimitCallback,
      getTierConfig,
    }),
    [subscription, isLoading, isSubscribed, subscriptionTier, canAccessFeature, checkAsinLimitCallback, getTierConfig]
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => {
  const context = React.useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

